import type { FC } from "react";
import { useEffect, useState, useRef } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Box,
  Grid,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
} from "@material-ui/core";
import { IColumn } from "src/components/dataTable/tableModel";
import DataTable from "src/components/dataTable/dataTable";
import { useSnackbar } from "notistack";
import RemoveElement from "src/hooks/useRemoveElement";
import ChevronRightIcon from "src/icons/ChevronRight";
import { deleteWebMethods, getWebMethodsList } from "./ConnectionService";
import { Helmet } from "react-helmet-async";

const columns: IColumn[] = [
  { field: "webMethodName", headerName: "WebMethod Name", width: 200 },
  { field: "webMethodURL", headerName: "WebMethod URL", width: 200 },
  {
    field: "authenticationType",
    headerName: "Authentication Type",
    width: 100,
  },
  { field: "webMethodVerb", headerName: "Verb", width: 100 },
];
const children: IColumn[] = [
  { field: "createdBy", headerName: "Created By" },
  { field: "createdOn", headerName: "Created On", dataType: "Date" },
  { field: "editedBy", headerName: "Last Modified By" },
  { field: "editedOn", headerName: "Last Modified On", dataType: "Date" },
];

const WebMethodsList: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [webMethodsList, setWebMethodsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [instance, setInstance] = useState({
    flg: false,
    primaryKey: null,
    filterKey: "",
  });
  const [webMethodsInfo, setWebMethodsInfo] = useState<any | null>(null);
  const [open, setOpen] = useState(false);

  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchWebMethodsList = () => {
    setIsLoading(true);
    getWebMethodsList()
      .then((res: any) => {
        setWebMethodsList(res || "");
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchWebMethodsList();
  }, []);

  const onClickEdit = (data: any) => {
    navigate("/WebMethodsCreateEdit", {
      state: { webMethodKey: data.webMethodKey },
    });
  };

  const onClickDelete = (data) => {
    setWebMethodsInfo(data);
    setOpen(true);
  };

  const onClickCreateNew = () => {
    navigate("/WebMethodsCreateEdit");
  };

  const onDeleteAppSetting = async () => {
    await deleteWebMethods(webMethodsInfo.webMethodKey)
      .then(() => {
        setOpen(false);
        enqueueSnackbar("Web Method deleted successfully", {
          anchorOrigin: {
            horizontal: "right",
            vertical: "top",
          },
          variant: "success",
        });
        setWebMethodsList(
          webMethodsList.filter(
            (x) => x.webMethodKey !== webMethodsInfo.webMethodKey
          )
        );
      })
      .catch((err) => {
        Object.entries(err.errors).forEach(([, errMsg]: any) => {
          enqueueSnackbar(errMsg.errorMessage, {
            anchorOrigin: {
              horizontal: "right",
              vertical: "top",
            },
            variant: "error",
          });
        });
      });
    setOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>Web Methods</title>
      </Helmet>
      <Box>
        <Container maxWidth={false}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1100,
              opacity: 1,
              width: "100%",
              py: 1,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Grid alignItems="center" container justifyContent="space-between">
              <Grid item>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                >
                  <Link
                    color="textPrimary"
                    component={RouterLink}
                    to=""
                    variant="h5"
                  >
                    Configurations
                  </Link>
                  <Typography color="textPrimary" variant="h5">
                    Web Methods
                  </Typography>
                </Breadcrumbs>
              </Grid>
              <Grid item>
                <Box sx={{ my: 2 }}>
                  <Button
                    sx={{
                      background:
                        "linear-gradient(to bottom, #072f40, #072f40)",
                      minWidth: 100,
                    }}
                    variant="contained"
                    onClick={onClickCreateNew}
                  >
                    Create New
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Dialog
            open={open}
            onClose={(event, reason) => {
              if (reason !== "backdropClick") {
                setOpen(false);
              }
            }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to delete?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={onDeleteAppSetting} autoFocus>
                Yes
              </Button>
              <Button onClick={() => setOpen(false)}>No</Button>
            </DialogActions>
          </Dialog>
          <Card>
            <DataTable
              columns={columns}
              dataList={webMethodsList}
              isLoading={isLoading}
              infoText="View"
              onClickEdit={onClickEdit}
              onClickDelete={onClickDelete}
              childrenColumns={children}
            />
            <RemoveElement
              instance={instance}
              setInstance={setInstance}
              setListArray={setWebMethodsList}
              apiURL="WebMethod/DeleteWebMethod/"
            />
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default WebMethodsList;
